import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Grid,
    Table,
    TableBody,
    TableHead,
    TableRow,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CircularProgress,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import axios from 'axios';
import toast from 'react-hot-toast';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useHistory, useLocation } from 'react-router-dom';
import ErrorFallback from 'components/error-fallback';
import { ErrorBoundary } from 'react-error-boundary';
import { endPoints } from 'pages/dlops-dashboard/api/endpoint';
import { PEPVIGIL, retrainingTagsKey, smartSamplingTagsKey } from 'pages/dlops-dashboard/utils/constant';
import { commonStyles as styles } from 'assets/style';
import { Item, StyledTableCell, ProgressIcon, style } from '../style';
import AccessToken from '../components/accessToken/accessToken';
import StyleTextField from '../styleTextField';
import breadcrumbIcon from '../../../assets/images/dlopsImages/arrow_forward.svg';
import { filterRetrainingTags } from '../utils';

const EditExistingModelTags = () => {
    const { getAccessToken } = AccessToken();
    const [addtags, setAddTags] = useState([]);
    const history = useHistory();
    const location = useLocation();
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [deleteOpenPopUp, setDeletePopUp] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState(null);
    const [allSelected, setAllSelected] = useState(false);
    const [errors, setErrors] = useState({});
    const [openPopup, setOpenPopup] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [isLoading, setLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [showAccessFields, setShowAccessFields] = useState(false);
    const [isSmartFilteringEnabled, setIsSmartFilteringEnabled] = useState(false);
    const [smartFilteringErrors, setSmartFilteringErrors] = useState({});
    const activeModelData = Array.isArray(location.state.state?.tags) ? filterRetrainingTags(location.state.state?.tags, retrainingTagsKey) : [];
    const [modelType, setModelType] = useState('');
    const [segmentationSubType, setSegmentationSubType] = useState('');

    // Add smart filtering state
    const [smartFiltering, setSmartFiltering] = useState([
        {
            name: 'Exported CSV File Location',
            mkey: 'output_csv_path',
            tooltipInfo: 'Defines the location where the new CSV file, generated after the smart filtering process, will be stored.',
            mvalue: '',
            helpText: 'Example: https://storageaccount.blob.core.windows.net/container-name/folder/file.csv',
        },
        {
            name: 'Processed Images Folder Path',
            mkey: 'images_output_path',
            tooltipInfo: 'Specifies the directory used to store the compressed (JPEG) images produced during the smart filtering process.',
            mvalue: '',
            helpText: 'Example: https://storageaccount.blob.core.windows.net/container-name/folder/file',
        },
    ]);

    const [tags, setTags] = useState([
        {
            name: 'BaseLine Datasource',
            mkey: 'baseline_datasource',
            tooltipInfo: 'A URL that provides access to a CSV file containing details about the training dataset used for the model',
            mvalue: '',
        },
        {
            name: 'Baseline Datasource Version',
            mkey: 'baseline_datasource_version',
            tooltipInfo:
                'The version number of the baseline datasource, indicating the specific iteration or update of the CSV file containing the training dataset details',
            mvalue: '',
        },
        {
            name: 'Storage Account',
            mkey: 'storage_account',
            tooltipInfo:
                'An identifier for the cloud storage account used to store data and files associated with the project, including models, datasets, and other resources',
            mvalue: '',
        },
        {
            name: 'Market',
            mkey: 'market',
            tooltipInfo:
                'The specific market or environment in which the model is being deployed for use, indicating the target audience or application context',
            mvalue: '',
        },
        {
            name: 'Container Name',
            mkey: 'container_name',
            tooltipInfo: 'The name of the storage container used to organize and manage data and files within a cloud storage account',
            mvalue: '',
        },
        { name: 'Framework', mkey: 'framework', tooltipInfo: 'The software framework or library used to develop and implement the model', mvalue: '' },
        {
            name: 'Drift Distance Threshold',
            mkey: 'drift_distance_threshold',
            tooltipInfo:
                'Set the threshold value for the drift monitor. If the drift distance exceeds this value, the monitor will trigger. The default value as per Arize is 2.',
            mvalue: 2,
        },
    ]);

    const retrainingData = JSON.parse(localStorage.getItem('retraining_data')) || [];
    const [retrainingTags, setRetrainingTags] = useState([
        {
            name: 'Retraining Storage Account',
            mkey: 'retraining_storage_acc_name',
            tooltipInfo: 'Name of the Azure Storage Account.',
            mvalue: retrainingData?.training_storage_acc_name,
        },
        {
            name: 'Retraining Container',
            mkey: 'retraining_container_name',
            tooltipInfo: 'Name of the blob container to store drifted images.',
            mvalue: retrainingData?.training_container_name,
        },
        {
            name: 'Retraining Destination Path',
            mkey: 'retraining_blob_path',
            tooltipInfo: 'Subdirectory within the container where images will be saved.',
            mvalue: retrainingData?.training_blob_path,
        },
        {
            name: 'Retraining SAS Token',
            mkey: 'retraining_sas_token',
            tooltipInfo: 'Shared Access Signature token for secure access.',
            mvalue: retrainingData?.training_sas_token,
        },
    ]);
    const [smartSampling, setSmartSampling] = useState([
        {
            name: 'Source Account',
            mkey: 'source_account',
            tooltipInfo: 'Source storage account for smart sampling.',
            mvalue: '',
        },
        {
            name: 'Source Container',
            mkey: 'source_container',
            tooltipInfo: 'Source container for smart sampling.',
            mvalue: '',
        },
        {
            name: 'Source Subpath',
            mkey: 'source_subpath',
            tooltipInfo: 'Source subpath for smart sampling.',
            mvalue: '',
        },
        {
            name: 'Output Account',
            mkey: 'output_account',
            tooltipInfo: 'Output storage account for smart sampling.',
            mvalue: '',
        },
        {
            name: 'Output Container',
            mkey: 'output_container',
            tooltipInfo: 'Output container for smart sampling.',
            mvalue: '',
        },
        {
            name: 'Output Subpath',
            mkey: 'output_subpath',
            tooltipInfo: 'Output subpath for smart sampling.',
            mvalue: '',
        },
    ]);
    // Helper function to validate URLs
    const isValidURL = (url) => {
        const pattern = /^https:\/\/[a-zA-Z0-9_-]+\.blob\.core\.windows\.net\/[a-zA-Z0-9_-]+(\/[a-zA-Z0-9_.-]+)*\/[a-zA-Z0-9_.-]+\.[a-z]+$/;
        return pattern.test(url);
    };

    useEffect(() => {
        if (activeModelData.length > 0) {
            // Update localStorage whenever activeModelData changes
            localStorage.setItem('activeModelData', JSON.stringify(activeModelData));
        }
    }, [activeModelData]);

    useEffect(() => {
        if (Array.isArray(location.state.state?.tags)) {
            const activeModelDataFromState = location.state.state?.tags;
            const isNeedRemovedData = true;

            const retrainingTagsFromActiveModelState = filterRetrainingTags(activeModelDataFromState, retrainingTagsKey, isNeedRemovedData);

            if (retrainingTagsFromActiveModelState.every((tag) => retrainingTagsKey.includes(tag.key))) {
                localStorage.setItem('retrainingTagsData', JSON.stringify(retrainingTagsFromActiveModelState));
            } else {
                const defaultRetrainingData = retrainingTags.map((tag) => ({ key: tag.mkey, value: tag.mvalue }));
                localStorage.setItem('retrainingTagsData', JSON.stringify(defaultRetrainingData));
            }
        }
    }, []);

    useEffect(() => {
        if (Array.isArray(location.state.state?.tags)) {
            const activeModelDataFromState = location.state.state?.tags;
            const isNeedRemovedData = true;

            const smartSamplingTagsFromActiveModelState = filterRetrainingTags(activeModelDataFromState, smartSamplingTagsKey, isNeedRemovedData);

            if (smartSamplingTagsFromActiveModelState.every((tag) => smartSamplingTagsKey.includes(tag.key))) {
                localStorage.setItem('smartSamplingTagsData', JSON.stringify(smartSamplingTagsFromActiveModelState));
            } else {
                const defaultSmartSamplingData = smartSampling.map((tag) => ({ key: tag.mkey, value: tag.mvalue }));
                localStorage.setItem('smartSamplingTagsData', JSON.stringify(defaultSmartSamplingData));
            }
        }
    }, []);
    useEffect(() => {
        const savedActiveModelData = localStorage.getItem('activeModelData');
        const savedActiveModelRetrainingData = JSON.parse(localStorage.getItem('retrainingTagsData')) || [];

        const parsedSavedData = savedActiveModelData ? JSON.parse(savedActiveModelData) : [];
        const parsedActiveModelData = activeModelData.length > 0 ? activeModelData : parsedSavedData;
        if (parsedActiveModelData.length > 0) {
            const modelTypeTag = parsedActiveModelData.find((item) => item.key === 'model_type');
            const segmentationSubTypeTag = parsedActiveModelData.find((item) => item.key === 'segmentation_sub_type');
            setModelType(modelTypeTag?.value || '');
            setSegmentationSubType(segmentationSubTypeTag?.value || '');

            // Update tags based on activeModelData or savedActiveModelData
            const modelMetricsTag = parsedActiveModelData.find((item) => item.key === 'model_metrics');
            const modelMetricsKeys = modelMetricsTag?.value?.replace(/[[\]']/g, '')?.split(/\s+/) ?? [];

            // Update default tags
            const updatedTags = tags.map((tag) => {
                const matchedTag = parsedActiveModelData.find((item) => item.key === tag.mkey);
                return {
                    ...tag,
                    mvalue: matchedTag ? matchedTag.value : tag.mvalue,
                };
            });

            // Update smart filtering tags from response data
            const updatedSmartFiltering = smartFiltering.map((tag) => {
                const matchedTag = parsedActiveModelData.find((item) => item.key === tag.mkey);
                let updatedValue = tag.mvalue;
                if (matchedTag) {
                    updatedValue = matchedTag.value === 'N/A' ? '' : matchedTag.value;
                }
                return {
                    ...tag,
                    mvalue: updatedValue,
                };
            });

            // Update smart sampling tags from response data - FIXED
            const updatedSmartSampling = smartSampling.map((tag) => {
                const matchedTag = parsedActiveModelData.find((item) => item.key === tag.mkey);
                let updatedValue = tag.mvalue;
                if (matchedTag) {
                    updatedValue = matchedTag.value === 'N/A' ? '' : matchedTag.value;
                }
                return {
                    ...tag,
                    mvalue: updatedValue,
                };
            });

            // Check if smart filtering should be enabled
            const hasSmartFilteringValues = updatedSmartFiltering.some((tag) => tag.mvalue && tag.mvalue.trim() !== '' && tag.mvalue !== 'N/A');
            setIsSmartFilteringEnabled(hasSmartFilteringValues);

            // Define excluded keys - make sure to include all smart sampling keys
            const excludedKeys = [
                'model_metrics',
                'accuracy',
                'model_type',
                'distribution_list',
                'project_name',
                'output_csv_path',
                'images_output_path',
                // Add all smart sampling keys to excluded
                'source_account',
                'source_container',
                'source_subpath',
                'output_account',
                'output_container',
                'output_subpath',
                'segmentation_sub_type',
            ];
            const allExcludedKeys = new Set([...excludedKeys, ...modelMetricsKeys]);

            // Only find truly unmatched tags (not in any of our predefined arrays)
            const unmatchedTags = parsedActiveModelData
                .filter(
                    (item) =>
                        !updatedTags.some((tag) => tag.mkey === item.key) &&
                        !updatedSmartFiltering.some((tag) => tag.mkey === item.key) &&
                        !updatedSmartSampling.some((tag) => tag.mkey === item.key) &&
                        !allExcludedKeys.has(item.key)
                )
                .map((item) => ({
                    name: item.key.replace(/_/g, ' '),
                    mkey: item.key,
                    mvalue: item.value,
                }));

            // Update retraining tags
            if (savedActiveModelRetrainingData.length > 0) {
                const updatedRetrainingTags = retrainingTags.map((tag) => {
                    const matchedTag = savedActiveModelRetrainingData.find((item) => item?.key === tag?.mkey);
                    return matchedTag ? { ...tag, mvalue: matchedTag?.value } : tag;
                });
                setRetrainingTags(updatedRetrainingTags);
            }

            // Set all the states
            setTags(updatedTags);
            setSmartFiltering(updatedSmartFiltering);
            setSmartSampling(updatedSmartSampling); // This was missing!
            setAddTags((prevAddTags) => [...prevAddTags, ...unmatchedTags]);
        }
    }, []);

    const handleSmartFilteringInputChange = (index, field, value) => {
        const updatedTags = [...smartFiltering];
        updatedTags[index][field] = value;
        setSmartFiltering(updatedTags);

        const otherIndex = index === 0 ? 1 : 0;
        const otherFieldValue = updatedTags[otherIndex].mvalue;

        // Check if either field has a value to auto-enable smart filtering
        const hasAnyValue = updatedTags.some((tag) => tag.mvalue && tag.mvalue.trim() !== '');
        setIsSmartFilteringEnabled(hasAnyValue);

        // Clear errors for both fields first
        setSmartFilteringErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[index];
            delete newErrors[otherIndex];
            return newErrors;
        });

        // Set new errors if needed
        if (value.trim() && !otherFieldValue.trim()) {
            setSmartFilteringErrors((prevErrors) => ({
                ...prevErrors,
                [otherIndex]: 'This field is required when other smart filtering fields are filled.',
            }));
        }

        // Validate URL for output_csv_path
        if (updatedTags[index].mkey === 'output_csv_path' && value && !isValidURL(value)) {
            setSmartFilteringErrors((prevErrors) => ({
                ...prevErrors,
                [index]: 'Please enter a valid CSV file URL.',
            }));
        }
    };

    const handleAddNewRow = () => {
        setAddTags([...addtags, { name: '', mkey: '', mvalue: '', checked: false }]);
    };
    const handleClosePopup = () => {
        setOpenPopup(false);
        setShowAccessFields(false);
        if (isSuccess) {
            localStorage.removeItem('create_project');
            localStorage.removeItem('editModelFormData');
            localStorage.removeItem('createNewtags');
            localStorage.removeItem('initialModelData');
            localStorage.removeItem('activeModelData');
            localStorage.removeItem('retrainingTagsData');
            history.push('/pepvigil-model-versions');
        }
    };
    const handleCancel = () => {
        history.push('/pepvigil-model-versions');
        localStorage.removeItem('initialModelData');
        localStorage.removeItem('editModelFormData');
        localStorage.removeItem('activeModelData');
        localStorage.removeItem('retrainingTagsData');
    };
    const handleBack = () => {
        history.push('/pepvigil-edit-existing-model');
    };
    const updateActiveModelData = (tagsData, addtagsData, smartFilteringData, smartSamplingData) => {
        const savedActiveModelData = JSON.parse(localStorage.getItem('activeModelData')) || [];
        const modelDataMap = savedActiveModelData.reduce((acc, item) => {
            acc[item.key] = item.value;
            return acc;
        }, {});
        [...tagsData, ...addtagsData, ...smartFilteringData, ...smartSamplingData].forEach((tag) => {
            if (tag.mkey && tag.mvalue !== undefined) {
                modelDataMap[tag.mkey] = tag.mvalue;
            }
        });
        const updatedModelData = Object.keys(modelDataMap).map((key) => ({
            key,
            value: modelDataMap[key],
        }));
        localStorage.setItem('activeModelData', JSON.stringify(updatedModelData));
    };

    const updateRetrainingTagsData = (retrainingTagsData) => {
        const savedRetrainingTags = JSON.parse(localStorage.getItem('retrainingTagsData')) || [];

        const retrainingDataMap = savedRetrainingTags.reduce((acc, item) => {
            acc[item.key] = item.value;
            return acc;
        }, {});

        retrainingTagsData.forEach((tag) => {
            retrainingDataMap[tag.mkey] = tag.mvalue;
        });

        const updatedRetrainingTags = Object.keys(retrainingDataMap).map((key) => ({
            key,
            value: retrainingDataMap[key],
        }));

        localStorage.setItem('retrainingTagsData', JSON.stringify(updatedRetrainingTags));
    };

    const handleInputChange = (index, field, value) => {
        const updatedTags = [...tags];
        updatedTags[index][field] = value;
        setTags(updatedTags);
        updateActiveModelData(updatedTags, addtags);
        if (value) {
            setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[index];
                return newErrors;
            });
        }
    };
    const handleInputTags = (index, field, value) => {
        const updatedRows = addtags.map((row, i) => {
            // When updating the name field, also update the mkey field
            if (i === index) {
                if (field === 'name') {
                    return { ...row, name: value, mkey: value }; // Update both name and mkey
                }
                return { ...row, [field]: value }; // Update the specified field
            }
            return row; // Return unchanged row
        });
        setAddTags(updatedRows);
        updateActiveModelData(tags, updatedRows);
    };

    const handleRetrainingInputChange = (index, field, value) => {
        const updatedTags = [...retrainingTags];
        updatedTags[index][field] = value;
        setRetrainingTags(updatedTags);
        updateRetrainingTagsData(updatedTags);
    };

    const handleSamplingInputChange = (index, field, value) => {
        const updatedTags = [...smartSampling];
        updatedTags[index][field] = value;
        setSmartSampling(updatedTags);
        updateActiveModelData(tags, addtags, smartFiltering, updatedTags);
        if (value) {
            setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[`sampling_${index}`];
                return newErrors;
            });
        }
    };

    const handleNavigation = () => {
        history.push('/pepvigil-projects');
    };
    const handleRowCheck = (index) => {
        const updatedRows = addtags.map((row, i) => (i === index ? { ...row, isChecked: !row.isChecked } : row));
        setAddTags(updatedRows);
    };

    const handleDeleteRow = (index) => {
        setDeleteIndex(index);
        setDeletePopUp(true);
    };
    const deleteRow = (index) => {
        const updatedRows = addtags.filter((_, i) => i !== index);
        setAddTags(updatedRows);
    };
    const handleCancelDelete = () => {
        setDeletePopUp(false);
    };
    const handleDeleteAllSelected = () => {
        setDeletePopUp(true);
        setDeleteIndex('SelectedAll');
    };
    const handleSelectAll = (event) => {
        const { checked } = event.target;
        setAllSelected(checked);
        setIsAllChecked(checked);
        setAddTags(addtags.map((row) => ({ ...row, checked, isChecked: checked })));
    };
    const handleConfirmDelete = () => {
        if (deleteIndex === 'SelectedAll') {
            setAddTags(addtags.filter((row) => !row.isChecked));
        } else {
            deleteRow(deleteIndex);
        }
        setDeletePopUp(false);
        setAllSelected(false);
        setIsAllChecked(false);
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100vh' }}>
                <CircularProgress color="inherit" size={16} sx={ProgressIcon.circularProgressColor} />
                <Typography variant="body2" sx={ProgressIcon.circularProgressColor} style={{ marginLeft: '10px' }}>
                    Loading
                </Typography>
            </Box>
        );
    }

    const handleValidation = () => {
        let formIsValid = true;
        const newErrors = {};
        const smartFilteringTagsErrors = {};

        // Smart filtering validation
        if (isSmartFilteringEnabled) {
            const filledFieldsCount = smartFiltering.filter((tag) => tag.mvalue.trim() !== '').length;
            smartFiltering.forEach((tag, index) => {
                const isFieldEmpty = !tag.mvalue.trim();
                const hasOtherFilledFields = filledFieldsCount > 0;

                if (isFieldEmpty && hasOtherFilledFields) {
                    formIsValid = false;
                    smartFilteringTagsErrors[index] = 'This field is required when smart filtering is enabled and other fields are filled.';
                } else if (tag.mkey === 'output_csv_path' && tag.mvalue && !isValidURL(tag.mvalue)) {
                    formIsValid = false;
                    smartFilteringTagsErrors[index] = 'Please enter a valid CSV file URL.';
                }
            });
        }

        tags.forEach((tag, index) => {
            switch (tag.mkey) {
                case 'baseline_datasource':
                    if (!tag.mvalue) {
                        formIsValid = false;
                        newErrors[index] = 'Baseline datasource is required.';
                    } else if (!isValidURL(tag.mvalue)) {
                        formIsValid = false;
                        newErrors[index] = 'Please enter a valid URL.';
                    }
                    break;

                case 'baseline_datasource_version':
                    if (!tag.mvalue) {
                        formIsValid = false;
                        newErrors[index] = 'Baseline datasource version is required.';
                    } else if (!/^\d+(\.\d+)?$/.test(tag.mvalue)) {
                        formIsValid = false;
                        newErrors[index] = 'Please enter a valid number or decimal.';
                    }
                    break;
                case 'desc_version':
                    if (!tag.mvalue) {
                        formIsValid = false;
                        newErrors[index] = 'Description version is required.';
                    }
                    break;
                case 'description_model':
                case 'storage_account':
                case 'market':
                case 'container_name':
                case 'framework':
                    if (!tag.mvalue) {
                        formIsValid = false;
                        newErrors[index] = `${tag.mkey.replace(/_/g, ' ')} is required.`;
                    }
                    break;
                case 'drift_distance_threshold':
                    if (Number.isNaN(tag.mvalue) || tag.mvalue < 0.5 || tag.mvalue > 5 || !/^\d+(\.\d+)?$/.test(tag.mvalue)) {
                        newErrors[index] = 'Please enter a number between 0.5 and less than 5';
                    }
                    break;

                default:
                    break;
            }
        });

        addtags.forEach((tag, index) => {
            if (!tag.name) {
                formIsValid = false;
                newErrors[`add_${index}_name`] = 'Name is required.';
            }
            if (!tag.mvalue) {
                formIsValid = false;
                newErrors[`add_${index}_mvalue`] = 'Value is required.';
            }
        });

        // Update the errors state
        setErrors(newErrors);
        setSmartFilteringErrors(smartFilteringTagsErrors);

        return formIsValid;
    };

    const updateEditExistingModel = async (accessTokenPromise, payload) => {
        try {
            const accessToken = await accessTokenPromise;
            const response = await axios.put(endPoints.editExistingModel, payload, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    Accept: 'application/json',
                },
            });
            return response?.data;
        } catch (error) {
            return error;
        }
    };
    const handleSaveTags = async () => {
        if (handleValidation()) {
            updateActiveModelData(tags, addtags);
            toast.success('Meta Data Tags Saved Successfully', {
                position: 'top-right',
                autoClose: 3000,
            });
        }
    };

    // Update the classifyChanges function to include smart filtering tags
    const classifyChanges = (initialTags, currentTags, currentAddTags, remainingTags, currentSmartFilteringTags) => {
        const changes = {
            create: [],
            update: [],
            delete: [],
        };

        // Convert initialTags into a map for easier lookup
        const initialTagsMap = initialTags?.tags.reduce((acc, tag) => {
            if (tag.key && tag.value) {
                // Ensures only valid keys and values are added
                acc[tag.key] = tag.value;
            }
            return acc;
        }, {});

        // Combine currentTags, currentAddTags, remainingTags, and smartFilteringTags for processing
        const currentTagKeys = new Set([...currentTags, ...currentAddTags, ...remainingTags, ...currentSmartFilteringTags].map((tag) => tag.mkey));

        const ignoredKeys = [
            'storage_account',
            'container_name',
            'project_name',
            'retraining_sas_token',
            'retraining_container_name',
            'retraining_blob_path',
            'retraining_storage_acc_name',
            'source_account',
            'source_container',
            'source_subpath',
            'output_account',
            'output_container',
            'output_subpath',
        ];
        const excludedKeys = ['desc_version', 'description_model'];

        // Handle updates and new tags in the main tags list
        [...currentTags, ...remainingTags, ...currentSmartFilteringTags].forEach((tag) => {
            if (ignoredKeys.includes(tag.mkey)) {
                return; // Skip processing for these keys
            }

            // Only consider tags with both key and value defined
            if (tag.mkey && tag.mvalue) {
                const initialValue = initialTagsMap[tag.mkey];
                if (initialValue === undefined) {
                    // This is a new tag; add only if both key and value are defined
                    changes.create.push({
                        operation: 'create',
                        tag: [
                            {
                                key: tag.mkey,
                                value: tag.mvalue,
                            },
                        ],
                    });
                } else if (initialValue !== tag.mvalue) {
                    // This is an updated tag with old and new values
                    changes.update.push({
                        operation: 'update',
                        tag: [
                            {
                                old_key: tag.mkey,
                                old_value: initialValue,
                                new_key: tag.mkey,
                                new_value: tag.mvalue,
                            },
                        ],
                    });
                }
                // Remove this tag from initialTagsMap to track deletions
                delete initialTagsMap[tag.mkey];
            }
        });

        // Handle additional tags (new rows) in currentAddTags
        currentAddTags.forEach((addTag) => {
            if (addTag.mkey && addTag.mvalue) {
                // Check for valid key and value
                const initialValue = initialTagsMap[addTag.mkey];
                if (initialValue === undefined) {
                    // This is a new tag, so create it
                    changes.create.push({
                        operation: 'create',
                        tag: [
                            {
                                key: addTag.mkey,
                                value: addTag.mvalue,
                            },
                        ],
                    });
                } else if (initialValue !== addTag.mvalue) {
                    // This is an updated tag
                    changes.update.push({
                        operation: 'update',
                        tag: [
                            {
                                old_key: addTag.mkey,
                                old_value: initialValue,
                                new_key: addTag.mkey,
                                new_value: addTag.mvalue,
                            },
                        ],
                    });
                }
                // Remove this tag from initialTagsMap to track deletions
                delete initialTagsMap[addTag.mkey];
            }
        });

        // Any remaining tags in initialTagsMap are considered deleted
        Object.keys(initialTagsMap).forEach((key) => {
            if (!ignoredKeys.includes(key) && initialTagsMap[key] && !currentTagKeys.has(key) && !excludedKeys.includes(key)) {
                changes.delete.push({
                    operation: 'delete',
                    tag: [
                        {
                            key,
                            value: initialTagsMap[key],
                        },
                    ],
                });
            }
        });

        return changes;
    };

    const handleSubmit = async () => {
        if (handleValidation()) {
            // Proceed with the submission
            const tagsToSave = tags.map(({ mkey, mvalue }) => ({ mkey, mvalue }));
            localStorage.setItem('createNewtags', JSON.stringify(tagsToSave));
            const modelFormData = JSON.parse(localStorage.getItem('editModelFormData')) || {};
            const initialModelDatas = JSON.parse(localStorage.getItem('initialModelData')) || [];
            const initialModelDesc = initialModelDatas.description || '';
            const currentModelDesc = modelFormData.model_desc || '';
            const modelDescChanged = initialModelDesc !== currentModelDesc;
            const currentTags = [...tags, ...retrainingTags, ...smartSampling].map(({ mkey, mvalue }) => ({ mkey, mvalue }));
            const currentAddTags = addtags.map(({ mkey, mvalue }) => ({
                // eslint-disable-line
                mkey,
                mvalue,
            }));

            // Include smart filtering tags in the payload
            const smartFilteringTagsToSave = smartFiltering.map(({ mkey, mvalue }) => ({
                mkey,
                mvalue: !mvalue ? 'N/A' : mvalue,
            }));

            // Get retraining data from localStorage
            const storedRetrainingData = JSON.parse(localStorage.getItem('retraining_data'));

            const storedSmartSamplingData = JSON.parse(localStorage.getItem('smart_sampling_data'));

            // eslint-disable-next-line no-console
            console.log(storedSmartSamplingData);

            // Prepare updates for retraining data
            const retrainingUpdates = [];
            if (storedRetrainingData) {
                const retrainingKeys = ['retraining_sas_token', 'retraining_container_name', 'retraining_blob_path', 'retraining_storage_acc_name'];
                const newValues = Object.fromEntries(retrainingKeys.map((key) => [key, retrainingTags.find((tag) => tag.mkey === key)?.mvalue || '']));
                retrainingKeys.forEach((key) => {
                    const storedKey = key.replace('retraining_', 'training_'); // match storedRetrainingData field names
                    if (storedRetrainingData[storedKey] !== newValues[key]) {
                        retrainingUpdates.push({
                            old_key: key,
                            old_value: storedRetrainingData[storedKey],
                            new_key: key,
                            new_value: newValues[key],
                        });
                    }
                });
                if (retrainingUpdates.length > 0) {
                    const updatedRetrainingData = {
                        ...storedRetrainingData,
                        updated_date: new Date().toISOString(),
                    };
                    // eslint-disable-next-line camelcase
                    retrainingUpdates.forEach(({ old_key, new_value }) => {
                        const storedKey = old_key.replace('retraining_', 'training_');
                        // eslint-disable-next-line camelcase
                        updatedRetrainingData[storedKey] = new_value;
                    });

                    localStorage.setItem('retraining_data', JSON.stringify(updatedRetrainingData));
                }
            }

            const smartSamplingUpdates = [];
            if (storedSmartSamplingData) {
                const smartSamplingKeys = ['source_account', 'source_container', 'source_subpath', 'output_account', 'output_container', 'output_subpath'];
                const newValues = Object.fromEntries(smartSamplingKeys.map((key) => [key, smartSampling.find((tag) => tag.mkey === key)?.mvalue || '']));
                smartSamplingKeys.forEach((key) => {
                    if (storedSmartSamplingData[key] !== newValues[key]) {
                        smartSamplingUpdates.push({
                            old_key: key,
                            old_value: storedSmartSamplingData[key],
                            new_key: key,
                            new_value: newValues[key],
                        });
                    }
                });
                if (smartSamplingUpdates.length > 0) {
                    const updatedSmartSamplingData = { ...storedSmartSamplingData, updated_date: new Date().toISOString() };
                    smartSamplingUpdates.forEach(({ old_key: key, new_value: newValue }) => {
                        updatedSmartSamplingData[key] = newValue;
                    });
                    localStorage.setItem('smart_sampling_data', JSON.stringify(updatedSmartSamplingData));
                }
            }

            // Include smart filtering tags in classification
            const {
                create,
                update,
                delete: deleteTags,
            } = classifyChanges(initialModelDatas, currentTags, currentAddTags, modelFormData.tags, smartFilteringTagsToSave);
            // } = classifyChanges(initialModelDatas, currentTags, currentAddTags, modelFormData.tags, smartFilteringTagsToSave);

            // Add retraining updates to the main updates array
            if (retrainingUpdates.length > 0) {
                update.push({
                    operation: 'update',
                    tag: retrainingUpdates,
                });
            }

            if (smartSamplingUpdates.length > 0) {
                update.push({
                    operation: 'update',
                    tag: smartSamplingUpdates,
                });
            }

            const tagPayload = {
                tags: [
                    create.length > 0 && {
                        operation: 'create',
                        tags: create.flatMap(({ tag }) => tag.map(({ key, value }) => ({ key, value }))),
                    },
                    update.length > 0 && {
                        operation: 'update',
                        tags: update.flatMap(({ tag }) =>
                            // eslint-disable-next-line camelcase
                            tag.map(({ old_key, old_value, new_key, new_value }) => ({ old_key, old_value, new_key, new_value }))
                        ),
                    },
                    deleteTags.length > 0 && {
                        operation: 'delete',
                        tags: deleteTags.flatMap(({ tag }) => tag.map(({ key, value }) => ({ key, value }))),
                    },
                ].filter(Boolean),
            };

            const projectName = localStorage.getItem('project_name') || {};
            const payload = {
                project_name: projectName || '',
                model_name: modelFormData.model_name || '',
                model_version: String(modelFormData.model_version) || '',
                model_updatedby_gpid: modelFormData.model_updatedby_gpid || '',
                model_updatedby_user_details: modelFormData.model_updatedby_user_details,
                ...(modelDescChanged && { model_desc: currentModelDesc }),
                tags: tagPayload.tags,
            };

            try {
                setLoading(true);
                const response = await updateEditExistingModel(getAccessToken(), payload);

                if (response && response.status === 'success') {
                    setPopupMessage(<>Model updated successfully</>);
                    setIsSuccess(true);
                } else {
                    setPopupMessage('Failed to edit Existing Model.');
                    setIsSuccess(false);
                }
                setLoading(false);
                setOpenPopup(true);
            } catch (error) {
                setPopupMessage('Error saving data.');
                setIsSuccess(false);
                setLoading(false);
                setOpenPopup(true);
            }
        }
    };

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            {' '}
            <Grid container>
                <Grid item xs={12}>
                    <Typography variant="h5">
                        <Item sx={style.subHeader}>
                            <div
                                style={{
                                    // marginLeft: '28px',
                                    marginTop: '8px',
                                    fontSize: '14px',
                                    textAlign: 'left',
                                }}
                                className="contentContainer"
                            >
                                <span>{PEPVIGIL}</span>
                                <img src={breadcrumbIcon} alt=" bredcrumb" style={style.breadcrumbIcon} />
                                <span aria-hidden="true" style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => handleNavigation()}>
                                    Projects
                                </span>
                                <img src={breadcrumbIcon} alt=" bredcrumb" style={style.breadcrumbIcon} />
                                <span aria-hidden="true" style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => handleCancel()}>
                                    Model Versions
                                </span>
                                <img src={breadcrumbIcon} alt=" bredcrumb" style={style.breadcrumbIcon} />
                                <span aria-hidden="true" style={{ fontWeight: 'bold', cursor: 'pointer' }} onClick={() => handleBack()}>
                                    Edit Existing Model
                                </span>
                                <img src={breadcrumbIcon} alt=" bredcrumb" style={style.breadcrumbIcon} />
                                <span style={{ fontWeight: 'bold', color: '#005CBC' }}>Edit Model Tags</span>
                            </div>
                        </Item>
                    </Typography>
                </Grid>
            </Grid>
            <Box className="contentContainer">
                <Box
                    sx={{
                        top: '164px',
                        // left: '27px',
                        // width: '1500px',
                        height: '70px',
                        background: '#FFFFFF 0% 0% no-repeat padding-box',
                        borderRadius: '4px',
                        opacity: 1,
                        marginTop: '20px',
                        padding: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        // marginLeft: '100px',
                    }}
                >
                    <Typography sx={{ fontSize: '18px', fontWeight: '700', color: '#005CBC' }}>Edit Model Tags</Typography>
                </Box>
                <Dialog
                    open={openPopup}
                    onClose={handleClosePopup}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    PaperProps={{
                        sx: {
                            width: 600,
                            height: 'auto',
                            // padding: '20px',
                            borderRadius: '8px',
                        },
                    }}
                >
                    <Box sx={{ border: (theme) => `10px solid ${theme.palette.border.$color_border_default}` }}>
                        <DialogContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                {/* Conditionally render the success icon and popupMessage only if showAccessFields is false */}
                                {!showAccessFields && (
                                    <>
                                        {isSuccess && (
                                            <CheckCircleIcon
                                                sx={{
                                                    fontSize: '50px',
                                                    color: '#005CBC',
                                                    marginBottom: '0px',
                                                }}
                                            />
                                        )}
                                        <DialogTitle
                                            id="alert-dialog-title"
                                            sx={{
                                                marginBottom: '20px',
                                                fontSize: '16px',
                                                color: isSuccess ? 'black' : 'red', // Show title in red if failed
                                            }}
                                        >
                                            {isSuccess ? 'Updated' : 'Failed'}
                                        </DialogTitle>
                                        <Typography
                                            sx={{
                                                color: isSuccess ? 'black' : 'red', // Error message in red if failed
                                                fontSize: '14px',
                                                textAlign: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {popupMessage}
                                        </Typography>
                                        <DialogActions
                                            sx={{
                                                justifyContent: 'center',
                                                padding: '16px',
                                            }}
                                        >
                                            <Button
                                                onClick={handleClosePopup}
                                                sx={{ ...styles.modalSaveButton }}
                                                // sx={{
                                                //     backgroundColor: '#005CBC',
                                                //     width: '200px',
                                                //     height: '45px',
                                                //     color: 'white',
                                                //     '&:hover': {
                                                //         backgroundColor: '#005CBC',
                                                //     },
                                                // }}
                                            >
                                                Okay
                                            </Button>
                                        </DialogActions>
                                    </>
                                )}
                            </Box>
                        </DialogContent>
                    </Box>
                </Dialog>
                {/* <TeamMemberDrawer open={openDrawer} onClose={handleDrawerClose} onSubmit={handleDrawerSubmit} sx={{ width: '90%' }} /> */}
                <Box
                    sx={{
                        top: '164px',
                        // left: '27px',
                        // width: '1500px',
                        background: '#FFFFFF 0% 0% no-repeat padding-box',
                        borderRadius: '4px',
                        opacity: 1,
                        marginTop: '20px',
                        padding: '16px',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        // marginLeft: '100px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '20px',
                                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                                width: '1135px',
                                paddingBottom: '10px',
                            }}
                        >
                            <Typography
                                sx={{
                                    marginRight: '10px',
                                    fontSize: '16px',
                                    fontFamily: ["'Inter', sans-serif !important"],
                                    color: '#0047BA',
                                    fontWeight: '500',
                                }}
                            >
                                Meta Data Tags
                            </Typography>
                            <Box sx={{ display: 'flex', marginLeft: 'auto', gap: '10px' }}>
                                <Button
                                    sx={{ ...styles.modalSaveButton, height: '36px', width: '139px' }}
                                    // variant="contained"
                                    // sx={{
                                    //     backgroundColor: '#1378D4',
                                    //     width: '139px',
                                    //     height: '36px',
                                    //     borderRadius: '4px',
                                    // }}
                                    onClick={handleAddNewRow}
                                >
                                    Add New Tag
                                </Button>
                                <Button
                                    sx={{ ...styles.modalSaveButton, height: '36px', width: '139px' }}
                                    // variant="contained"
                                    // sx={{
                                    //     backgroundColor: '#1378D4',
                                    //     width: '139px',
                                    //     height: '36px',
                                    //     borderRadius: '4px',
                                    // }}
                                    onClick={handleSaveTags}
                                >
                                    Save Tags
                                </Button>
                                {allSelected && addtags.length >= 1 && (
                                    <Button
                                        sx={{
                                            ...styles.primaryMediumDangerButton,
                                            '&.MuiButton-root:hover': {
                                                backgroundColor: (theme) =>
                                                    `${theme.palette.background.$color_background_button_primary_danger_hover}!important`,
                                            },

                                            height: '36px',
                                            width: '187px',
                                        }}
                                        // variant="contained"
                                        // sx={{
                                        //     backgroundColor: '#D40020',
                                        //     borderRadius: '4px',
                                        //     width: '187px',
                                        // }}
                                        onClick={handleDeleteAllSelected}
                                    >
                                        Delete All Selected
                                    </Button>
                                )}
                            </Box>
                        </Box>
                        <Table>
                            <TableHead sx={{ backgroundColor: (theme) => theme.palette.cyan['90'], borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                                <TableRow>
                                    <StyledTableCell sx={{ width: '40px', padding: '4px 8px', color: '#005CBC' }}>
                                        <Checkbox sx={{ color: '#005CBC' }} checked={isAllChecked} onChange={handleSelectAll} disableRipple disableElevation />
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ color: '#005CBC', width: '200px', fontSize: '14px', padding: '12px 12px', fontWeight: '700' }}>
                                        Tag Name
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ color: '#005CBC', width: '100px', fontSize: '14px', padding: '12px 12px', fontWeight: '700' }}>
                                        Value
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ color: '#005CBC', width: '100px', fontSize: '14px', padding: '12px 12px', fontWeight: '700' }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tags.map((row, index) => (
                                    <TableRow key={index} sx={{ height: '40px' }}>
                                        <StyledTableCell sx={{ width: '40px', padding: '12px 12px' }} />
                                        <StyledTableCell sx={{ padding: '12px 12px', width: '200px' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {row.mkey !== 'drift_distance_threshold' ? (
                                                    <Typography sx={{ color: '#E4080A', marginRight: '4px' }}>*</Typography>
                                                ) : null}
                                                {row.name}
                                                {row.tooltipInfo && (
                                                    <Tooltip
                                                        title={row.tooltipInfo}
                                                        arrow
                                                        componentsProps={{
                                                            tooltip: { sx: { backgroundColor: '#272727', fontSize: '10px' } },
                                                            arrow: { sx: { color: '#272727' } },
                                                        }}
                                                    >
                                                        <InfoOutlinedIcon
                                                            sx={{
                                                                fontSize: '16px',
                                                                color: '#005CBC',
                                                                marginLeft: '8px',
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell sx={{ padding: '12px 12px', width: '100px' }}>
                                            <StyleTextField
                                                value={row.mvalue}
                                                onChange={(e) => handleInputChange(index, 'mvalue', e.target.value)}
                                                variant="filled"
                                                fullWidth={row.mkey === 'baseline_datasource'}
                                                disabled={row.mkey === 'storage_account' || row.mkey === 'container_name'}
                                                sx={{
                                                    // '& .MuiFilledInput-root': {
                                                    //     background: '#FFFFFF',
                                                    //     height: '32px',
                                                    //     padding: '4px',
                                                    // },
                                                    '&.MuiTextField-root': {
                                                        border: 'none',
                                                    },
                                                    width: row.mkey === 'baseline_datasource' ? '500px' : '200px',
                                                    border: `1px solid ${errors[index] ? 'd40020' : '#1378D4'}`,
                                                    // borderRadius: '4px',
                                                }}
                                                inputProps={{
                                                    style: { padding: '4px' },
                                                }}
                                            />
                                            {errors[index] && (
                                                <Typography sx={{ color: '#d40020', fontSize: '12px', marginTop: '4px' }}>{errors[index]}</Typography>
                                            )}
                                            {row.mkey === 'baseline_datasource' && (
                                                <Typography sx={{ fontSize: '10px', color: 'grey', marginTop: '4px' }}>
                                                    Example:
                                                    https://iaimlops.blob.core.windows.net/dlops-container/OC/baseline/dlops-model-oc/baseline_dlops-oc_20240425.csv
                                                </Typography>
                                            )}
                                        </StyledTableCell>
                                    </TableRow>
                                ))}

                                {addtags &&
                                    addtags.length > 0 &&
                                    addtags.map((row, index) => (
                                        <TableRow key={index}>
                                            <StyledTableCell sx={{ width: '40px', padding: '12px 12px' }}>
                                                <Checkbox disableElevation disableRipple checked={row.isChecked} onChange={() => handleRowCheck(index)} />
                                            </StyledTableCell>
                                            <StyledTableCell sx={{ padding: '12px 12px', width: '200px' }}>
                                                <StyleTextField
                                                    value={row.name}
                                                    onChange={(e) => handleInputTags(index, 'name', e.target.value)}
                                                    variant="filled"
                                                    sx={{
                                                        marginTop: '8px',
                                                        // '& .MuiFilledInput-root': {
                                                        //     background: '#FFFFFF',
                                                        //     height: '36px',
                                                        //     padding: '10px',
                                                        // },
                                                        width: '200px',
                                                        // border: '1px solid #1378D4',
                                                        borderRadius: '4px',
                                                    }}
                                                    inputProps={{
                                                        style: { padding: '4px' },
                                                    }}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell sx={{ padding: '12px 12px', width: '100px' }}>
                                                <StyleTextField
                                                    value={row.mvalue}
                                                    onChange={(e) => handleInputTags(index, 'mvalue', e.target.value)}
                                                    variant="filled"
                                                    sx={{
                                                        marginTop: '8px',
                                                        // '& .MuiFilledInput-root': {
                                                        //     background: '#FFFFFF',
                                                        //     height: '36px',
                                                        //     padding: '10px',
                                                        // },
                                                        width: '200px',
                                                        // border: '1px solid #1378D4',
                                                        // borderRadius: '4px',
                                                    }}
                                                    inputProps={{
                                                        style: { padding: '4px' },
                                                    }}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell sx={{ width: '100px', padding: '12px 12px' }}>
                                                {row.isChecked && (
                                                    <Typography
                                                        onClick={() => handleDeleteRow(index)}
                                                        sx={{ color: '#1378D4', textDecoration: 'underline', cursor: 'pointer' }}
                                                    >
                                                        Delete
                                                    </Typography>
                                                )}
                                            </StyledTableCell>
                                        </TableRow>
                                    ))}

                                {/* Enable Smart Filtering Section */}
                                <StyledTableCell sx={{ padding: '12px 12px', width: '100%' }} colSpan={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Tooltip
                                            title={
                                                <>
                                                    <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                                        Optimizes image size and standardizes formats to enable faster processing and improve model performance.{' '}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '12px', marginTop: '4px', fontWeight: '600' }}>
                                                        Use this only when the dataset contains images larger than 3 MB.
                                                    </Typography>
                                                </>
                                            }
                                            arrow
                                            placement="top-end"
                                        >
                                            <Typography
                                                sx={{
                                                    marginRight: '10px',
                                                    fontSize: '16px',
                                                    color: '#0047BA',
                                                    fontWeight: '500',
                                                }}
                                            >
                                                Enable Smart Filtering
                                                <Checkbox
                                                    size="small"
                                                    color="primary"
                                                    checked={isSmartFilteringEnabled}
                                                    onChange={(e) => {
                                                        setIsSmartFilteringEnabled(e.target.checked);
                                                        // Optional: Clear smart filtering fields when unchecked
                                                        if (!e.target.checked) {
                                                            setSmartFiltering((prev) => prev.map((tag) => ({ ...tag, mvalue: '' })));
                                                            setSmartFilteringErrors({});
                                                        }
                                                    }}
                                                />
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                </StyledTableCell>

                                {/* Smart Filtering Fields */}
                                {isSmartFilteringEnabled &&
                                    smartFiltering?.map((row, index) => {
                                        const hasAnyValue = smartFiltering.some((tag) => tag.mvalue.trim() !== '');
                                        const isRequired = hasAnyValue;

                                        return (
                                            <TableRow key={`smart-${index}`} sx={{ height: '40px' }}>
                                                <StyledTableCell sx={{ width: '40px', padding: '12px 12px' }} />
                                                <StyledTableCell sx={{ padding: '12px 12px', width: '200px' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        {isRequired && <Typography sx={{ color: '#E4080A', marginRight: '4px' }}>*</Typography>}
                                                        {row.name}
                                                        {row.tooltipInfo && (
                                                            <Tooltip
                                                                title={row.tooltipInfo}
                                                                arrow
                                                                componentsProps={{
                                                                    tooltip: { sx: { backgroundColor: '#272727', fontSize: '10px' } },
                                                                    arrow: { sx: { color: '#272727' } },
                                                                }}
                                                            >
                                                                <InfoOutlinedIcon
                                                                    sx={{
                                                                        fontSize: '16px',
                                                                        color: '#005CBC',
                                                                        marginLeft: '8px',
                                                                        cursor: 'pointer',
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </StyledTableCell>
                                                <StyledTableCell sx={{ padding: '12px 12px', width: '100px' }}>
                                                    <StyleTextField
                                                        value={row.mvalue}
                                                        onChange={(e) => handleSmartFilteringInputChange(index, 'mvalue', e.target.value)}
                                                        variant="filled"
                                                        sx={{
                                                            '&.MuiTextField-root': {
                                                                border: 'none',
                                                            },
                                                            width: '500px',
                                                            border: `1px solid ${smartFilteringErrors[index] ? '#d40020' : '#1378D4'}`,
                                                            opacity: isSmartFilteringEnabled ? 1 : 0.6,
                                                        }}
                                                        inputProps={{
                                                            style: { padding: '4px' },
                                                        }}
                                                        disabled={!isSmartFilteringEnabled}
                                                    />
                                                    {smartFilteringErrors[index] && (
                                                        <Typography sx={{ color: '#d40020', fontSize: '12px', marginTop: '4px' }}>
                                                            {smartFilteringErrors[index]}
                                                        </Typography>
                                                    )}
                                                    <Typography sx={{ fontSize: '10px', color: 'grey', mt: '4px' }}>{row.helpText}</Typography>
                                                </StyledTableCell>
                                            </TableRow>
                                        );
                                    })}
                                {(modelType === 'Segmentation' || segmentationSubType) && (
                                    <TableRow sx={{ height: '40px' }}>
                                        <StyledTableCell sx={{ padding: '12px 12px', width: '100%' }} colSpan={3}>
                                            <Typography
                                                sx={{
                                                    marginRight: '10px',
                                                    fontSize: '16px',
                                                    color: '#0047BA',
                                                    fontWeight: '500',
                                                }}
                                            >
                                                Smart Sampling Automated Pipeline Configuration
                                            </Typography>
                                        </StyledTableCell>
                                    </TableRow>
                                )}
                                {(modelType === 'Segmentation' || segmentationSubType) &&
                                    smartSampling?.map((row, index) => (
                                        <TableRow key={index} sx={{ height: '40px' }}>
                                            <StyledTableCell sx={{ width: '40px', padding: '12px 12px' }} />
                                            <StyledTableCell sx={{ padding: '12px 12px', width: '200px' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Typography sx={{ color: '#E4080A', marginRight: '4px' }}>*</Typography>
                                                    {row.name}
                                                    {row.tooltipInfo && (
                                                        <Tooltip
                                                            title={row.tooltipInfo}
                                                            arrow
                                                            componentsProps={{
                                                                tooltip: { sx: { backgroundColor: '#272727', fontSize: '10px' } },
                                                                arrow: { sx: { color: '#272727' } },
                                                            }}
                                                        >
                                                            <InfoOutlinedIcon
                                                                sx={{
                                                                    fontSize: '16px',
                                                                    color: '#005CBC',
                                                                    marginLeft: '8px',
                                                                    cursor: 'pointer',
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </StyledTableCell>
                                            <StyledTableCell sx={{ padding: '12px 12px', width: '100px' }}>
                                                <StyleTextField
                                                    value={row.mvalue}
                                                    onChange={(e) => handleSamplingInputChange(index, 'mvalue', e.target.value)}
                                                    variant="filled"
                                                    sx={{
                                                        '&.MuiTextField-root': { border: 'none' },
                                                        width: '200px',
                                                        border: `1px solid ${errors[`sampling_${index}`] ? '#d40020' : '#1378D4'}`,
                                                    }}
                                                    inputProps={{ style: { padding: '4px' } }}
                                                />
                                                {errors[`sampling_${index}`] && (
                                                    <Typography sx={{ color: '#d40020', fontSize: '12px', marginTop: '4px' }}>
                                                        {errors[`sampling_${index}`]}
                                                    </Typography>
                                                )}
                                            </StyledTableCell>
                                        </TableRow>
                                    ))}
                                <TableRow sx={{ height: '40px' }}>
                                    <StyledTableCell sx={{ padding: '12px 12px', width: '100%' }} colSpan={3}>
                                        <Typography
                                            sx={{
                                                marginRight: '10px',
                                                fontSize: '16px',
                                                color: '#0047BA',
                                                fontWeight: '500',
                                            }}
                                        >
                                            Retraining Data Storage Configuration
                                        </Typography>
                                    </StyledTableCell>
                                </TableRow>
                                {retrainingTags?.map((row, index) => (
                                    <TableRow key={index} sx={{ height: '40px' }}>
                                        <StyledTableCell sx={{ width: '40px', padding: '12px 12px' }} />
                                        <StyledTableCell sx={{ padding: '12px 12px', width: '200px' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {row.name}
                                                {row.tooltipInfo && (
                                                    <Tooltip
                                                        title={row.tooltipInfo}
                                                        arrow
                                                        componentsProps={{
                                                            tooltip: { sx: { backgroundColor: '#272727', fontSize: '10px' } },
                                                            arrow: { sx: { color: '#272727' } },
                                                        }}
                                                    >
                                                        <InfoOutlinedIcon
                                                            sx={{
                                                                fontSize: '16px',
                                                                color: '#005CBC',
                                                                marginLeft: '8px',
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell sx={{ padding: '12px 12px', width: '100px' }}>
                                            <StyleTextField
                                                value={row.mvalue}
                                                onChange={(e) => handleRetrainingInputChange(index, 'mvalue', e.target.value)}
                                                variant="filled"
                                                sx={{
                                                    '&.MuiTextField-root': {
                                                        border: 'none',
                                                    },
                                                    width: '200px',
                                                    border: `1px solid '#1378D4'`,
                                                    // borderRadius: '4px',
                                                }}
                                                inputProps={{
                                                    style: { padding: '4px' },
                                                }}
                                            />
                                        </StyledTableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <Box sx={{ display: 'flex', gap: '10px' }}>
                            <Button
                                variant="contained"
                                sx={{ ...styles.primaryMediumButton, marginTop: '50px' }}
                                // sx={{ backgroundColor: '#1378D4', marginTop: '50px' }}
                                onClick={handleBack}
                                disableRipple
                                disableElevation
                            >
                                Back
                            </Button>
                            <Button
                                variant="outlined"
                                sx={{ ...styles.secondaryMediumButton, marginTop: '50px' }}
                                // sx={{ marginTop: '50px' }}
                                onClick={handleCancel}
                                disableRipple
                                disableElevation
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                sx={{ ...styles.primaryMediumButton, marginTop: '50px', marginLeft: '500px' }}
                                // sx={{ backgroundColor: '#1378D4', marginTop: '50px', marginLeft: '500px' }}
                                onClick={handleSubmit}
                                disableRipple
                                disableElevation
                            >
                                Submit
                            </Button>
                        </Box>
                    </Box>
                </Box>
                <Dialog
                    open={deleteOpenPopUp}
                    onClose={handleCancelDelete}
                    PaperProps={{
                        sx: {
                            width: '480px',
                            // height: '200px',
                            background: '#FFFFFF 0% 0% no-repeat padding-box',
                            boxShadow: '0px 0px 6px #00000074',
                            borderRadius: '4px',
                            // padding: '16px',
                        },
                    }}
                >
                    <Box
                        sx={{
                            ...styles.modalWrapper,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        <DialogTitle sx={styles.modalTitle}>Confirm Delete</DialogTitle>
                        <DialogContent>
                            <DialogContentText sx={styles.modalContent}>Are you sure you want to delete the selected model tags?</DialogContentText>
                        </DialogContent>
                        <DialogActions sx={{ justifyContent: 'center', padding: '8px 16px', marginBottom: '10px' }}>
                            <Button
                                onClick={handleConfirmDelete}
                                disableElevation
                                disableRipple
                                sx={{ ...styles.modalSaveButton, width: '100px' }}
                                // color="primary"
                                // sx={{ background: '#1378D4 0% 0% no-repeat padding-box', width: '118px', height: '53px', color: 'white' }}
                            >
                                Yes
                            </Button>
                            <Button
                                onClick={handleCancelDelete}
                                sx={{ ...styles.modalCancelButton, width: '100px' }}
                                // color="primary"
                                // sx={{
                                //     width: '116px',
                                //     height: '53px',
                                //     background: '#FFFFFF 0% 0% no-repeat padding-box',
                                //     border: '1px solid #707070',
                                //     borderRadius: '6px',
                                //     color: '#000000',
                                // }}
                            >
                                No
                            </Button>
                        </DialogActions>
                    </Box>
                </Dialog>
            </Box>
        </ErrorBoundary>
    );
};

export default EditExistingModelTags;
